import { API_URL } from 'config';
import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { useNavigate } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import { GlassCard } from '../components/UI/GlassCard';
import { getStoredReferral } from '../utils/referral';
import './AuthPage.css';

const CAPTCHA_NATURAL_WIDTH = 304;
const CAPTCHA_NATURAL_HEIGHT = 78;
const RESEND_COOLDOWN = 60;

const AuthCaptcha = ({ recaptchaRef, onChange }) => {
  const wrapRef = useRef(null);
  const [scale, setScale] = useState(() => {
    if (typeof window === 'undefined') return 1;
    const cardWidth = Math.max(320, window.innerWidth - 32);
    return Math.max(0.72, Math.min(1, cardWidth / CAPTCHA_NATURAL_WIDTH));
  });

  useEffect(() => {
    if (!wrapRef.current || typeof window === 'undefined' || !window.ResizeObserver) return;

    const el = wrapRef.current;
    const updateScale = () => {
      const available = el.clientWidth;
      const s = Math.max(0.72, Math.min(1, available / CAPTCHA_NATURAL_WIDTH));
      setScale(s);
    };

    updateScale();
    const ro = new ResizeObserver(updateScale);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div
      className="auth-captcha-wrap"
      ref={wrapRef}
      style={{ height: CAPTCHA_NATURAL_HEIGHT * scale }}
    >
      <div
        className="auth-captcha-inner"
        style={{
          transform: `scale(${scale})`,
          transformOrigin: 'center top',
          width: CAPTCHA_NATURAL_WIDTH,
        }}
      >
        <ReCAPTCHA
          ref={recaptchaRef}
          sitekey="6LcSHkMtAAAAAOpxLo8SL9FQrqGc81fvm3rBGbcw"
          onChange={onChange}
        />
      </div>
    </div>
  );
};

export const AuthPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [regSuccessMessage, setRegSuccessMessage] = useState('');

  const [captchaToken, setCaptchaToken] = useState('');
  const recaptchaRef = useRef(null);
  const [authChecking, setAuthChecking] = useState(true);

  const [pendingReg, setPendingReg] = useState(null);
  const [loginPending, setLoginPending] = useState(null);
  const [code, setCode] = useState('');
  const [devCode, setDevCode] = useState('');
  const [resendIn, setResendIn] = useState(0);
  const [busy, setBusy] = useState(false);

  const codeMode = Boolean(pendingReg || loginPending);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    fetch(`${API_URL}/auth/check`, { method: 'GET', credentials: 'include' })
      .then((res) => {
        if (res.ok) {
          navigate('/cabinet');
        } else {
          setAuthChecking(false);
        }
      })
      .catch(() => setAuthChecking(false));
  }, [navigate]);

  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const timer = setInterval(() => setResendIn((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(timer);
  }, [resendIn]);

  const getPasswordStrength = (pwd) => {
    if (!pwd) return { score: 0, label: 'empty', color: '#ef4444', textKey: '' };
    let score = 0;
    if (pwd.length >= 8) score++;
    if (/[0-9]/.test(pwd)) score++;
    if (/[A-Z]/.test(pwd)) score++;
    if (/[^A-Za-z0-9]/.test(pwd)) score++;

    const levels = {
      0: { label: 'weak', color: '#ef4444', textKey: 'auth.passwordStrengthWeak' },
      1: { label: 'weak', color: '#ef4444', textKey: 'auth.passwordStrengthWeak' },
      2: { label: 'medium', color: '#f97316', textKey: 'auth.passwordStrengthMedium' },
      3: { label: 'medium', color: '#f97316', textKey: 'auth.passwordStrengthMedium' },
      4: { label: 'strong', color: '#10b981', textKey: 'auth.passwordStrengthStrong' },
    };
    return { score, ...levels[score] };
  };

  const strength = getPasswordStrength(password);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setError('');
    setRegSuccessMessage('');
    setCaptchaToken('');
    if (recaptchaRef.current) recaptchaRef.current.reset();
  };

  const resetCodeState = () => {
    setPendingReg(null);
    setLoginPending(null);
    setCode('');
    setDevCode('');
    setResendIn(0);
  };

  const handleBackFromCode = () => {
    resetCodeState();
    setError('');
  };

  const handleGoogleLogin = () => {
    window.location.href = `${API_URL}/auth/oauth/google`;
  };

  const extractError = (data) => {
    if (Array.isArray(data?.detail)) return data.detail[0].msg;
    if (typeof data?.detail === 'object' && data.detail !== null) {
      return data.detail.message || t('auth.errorUnknown');
    }
    return data?.detail || t('auth.errorUnknown');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setRegSuccessMessage('');

    if (!email || !password) {
      setError(t('auth.fillAllFields'));
      return;
    }

    if (!captchaToken) {
      setError(t('auth.captchaRequired'));
      return;
    }

    if (activeTab === 'register') {
      if (password !== confirmPassword) {
        setError(t('auth.passwordMismatch'));
        return;
      }
      if (strength.score < 2) {
        setError(t('auth.passwordWeak'));
        return;
      }
    }

    setBusy(true);
    try {
      const url = activeTab === 'login'
        ? `${API_URL}/auth/login`
        : `${API_URL}/auth/registration`;

      const bodyData = {
        email: email,
        password: password,
        captcha: captchaToken,
      };
      if (activeTab === 'register') {
        bodyData.ref = getStoredReferral() || undefined;
      }

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(bodyData),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        if (data.detail?.code === 'email_code_required' && data.detail.login_token) {
          setLoginPending({ login_token: data.detail.login_token, email });
          setDevCode(data.detail.dev_code || '');
          setResendIn(RESEND_COOLDOWN);
          return;
        }
        throw new Error(extractError(data));
      }

      if (activeTab === 'register') {
        setPendingReg({ pending_id: data.pending_id, email });
        setDevCode(data.dev_code || '');
        setResendIn(RESEND_COOLDOWN);
        return;
      }

      setSuccess(true);
      localStorage.removeItem('auth_check_block');
      setTimeout(() => {
        navigate('/cabinet');
      }, 1500);

    } catch (err) {
      setError(err.message);
      setCaptchaToken('');
      if (recaptchaRef.current) recaptchaRef.current.reset();
    } finally {
      setBusy(false);
    }
  };

  const handleConfirmCode = async (e) => {
    e.preventDefault();
    setError('');

    if (!code.trim()) {
      setError(t('auth.codeRequired'));
      return;
    }

    const url = pendingReg
      ? `${API_URL}/auth/registration/confirm`
      : `${API_URL}/auth/login/confirm`;

    const body = pendingReg
      ? { pending_id: pendingReg.pending_id, code: code.trim() }
      : { login_token: loginPending.login_token, code: code.trim() };

    setBusy(true);
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(extractError(data));
      }

      resetCodeState();
      setSuccess(true);
      localStorage.removeItem('auth_check_block');
      setTimeout(() => {
        navigate('/cabinet');
      }, 1500);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const handleResend = async () => {
    if (resendIn > 0 || !pendingReg) return;
    setError('');
    setBusy(true);
    try {
      const response = await fetch(`${API_URL}/auth/registration/resend`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ pending_id: pendingReg.pending_id }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(extractError(data));
      }
      setCode('');
      setDevCode(data.dev_code || '');
      setResendIn(RESEND_COOLDOWN);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const verifyEmail = pendingReg?.email || loginPending?.email || '';

  const renderVerifyStep = () => (
    <form onSubmit={handleConfirmCode} className="auth-form">
      <h4 className="auth-form-title">
        {pendingReg ? t('auth.verifyTitle') : t('auth.verifyLoginTitle')}
      </h4>

      <p className="auth-verify-intro">
        {t(pendingReg ? 'auth.verifyIntro' : 'auth.verifyLoginIntro', { email: verifyEmail })}
      </p>

      {error && (
        <div className="auth-error">
          <i className="fas fa-exclamation-circle mr-2"></i>
          {error}
        </div>
      )}

      {devCode && (
        <div className="auth-success-alert" style={{ color: '#f97316', marginBottom: '15px', fontSize: '13px' }}>
          <i className="fas fa-info-circle mr-2"></i>
          {t('auth.devCodeNotice', { code: devCode })}
        </div>
      )}

      <div className="form-group">
        <label><i className="fas fa-envelope-open-text mr-2"></i>{t('auth.codeLabel')}</label>
        <input
          type="text"
          required
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
          placeholder={t('auth.codePlaceholder')}
          className="auth-code-input"
          style={{ textAlign: 'center', fontSize: '20px', letterSpacing: '8px' }}
        />
      </div>

      <button type="submit" className="auth-submit-btn" disabled={busy}>
        <><i className="fas fa-check-circle mr-2"></i>{t('auth.confirmCode')}</>
      </button>

      {pendingReg && (
        <button
          type="button"
          className="auth-google-btn"
          onClick={handleResend}
          disabled={resendIn > 0 || busy}
        >
          <i className="fas fa-redo mr-2"></i>
          {resendIn > 0 ? t('auth.resendIn', { seconds: resendIn }) : t('auth.resendCode')}
        </button>
      )}

      <button type="button" className="auth-back-btn" onClick={handleBackFromCode}>
        <i className="fas fa-arrow-left mr-2"></i>
        {t('auth.changeEmail')}
      </button>
    </form>
  );

  return (
    <div className="auth-page">
      {authChecking ? (
        <div className="auth-loading-overlay">
          <div className="auth-loading-spinner" />
        </div>
      ) : (
        <>
          <div className="auth-bg-glow blob-1" />
          <div className="auth-bg-glow blob-2" />

          <div className="auth-container">
            <div className="auth-card-wrapper">
              <GlassCard className="auth-card" hoverEffect={false}>
                <div className="auth-brand">
                  <div className="auth-logo-icon">
                    <i className="fas fa-paper-plane"></i>
                  </div>
                  <span className="auth-logo-text">Telegram<span>PRIME</span></span>
                </div>

                {success ? (
              <div className="auth-success">
                <motion.div
                  className="success-icon-wrap"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ duration: 0.3, ease: 'easeOut' }}
                >
                  <i className="fas fa-check-circle" style={{ fontSize: '48px', color: '#10b981' }}></i>
                </motion.div>
                <h3>{t('auth.successMsg')}</h3>
                <p>{t('auth.welcome')}</p>
              </div>
            ) : codeMode ? (
              renderVerifyStep()
            ) : (
              <>
                <div className="auth-tabs">
                  <button
                    className={`auth-tab-btn ${activeTab === 'login' ? 'active' : ''}`}
                    onClick={() => handleTabChange('login')}
                  >
                    <i className="fas fa-sign-in-alt mr-2"></i>
                    {t('auth.login')}
                  </button>
                  <button
                    className={`auth-tab-btn ${activeTab === 'register' ? 'active' : ''}`}
                    onClick={() => handleTabChange('register')}
                  >
                    <i className="fas fa-user-plus mr-2"></i>
                    {t('auth.register')}
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="auth-form">
                  <h4 className="auth-form-title">{t('auth.title')}</h4>

                  {error && (
                    <div className="auth-error">
                      <i className="fas fa-exclamation-circle mr-2"></i>
                      {error}
                    </div>
                  )}

                  {regSuccessMessage && (
                    <div className="auth-success-alert" style={{ color: '#10b981', marginBottom: '15px', fontSize: '14px' }}>
                      <i className="fas fa-info-circle mr-2"></i>
                      {regSuccessMessage}
                    </div>
                  )}

                  <div className="form-group">
                    <label><i className="fas fa-envelope mr-2"></i>{t('auth.email')}</label>
                    <input
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="name@example.com"
                    />
                  </div>

                  <div className="form-group">
                    <label><i className="fas fa-lock mr-2"></i>{t('auth.password')}</label>
                    <input
                      type="password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                    />
                    {activeTab === 'register' && password && (
                      <div className="strength-meter">
                        <div className="strength-bar-bg">
                          <div
                            className="strength-bar-fill"
                            style={{
                              width: `${(strength.score / 4) * 100}%`,
                              backgroundColor: strength.color,
                            }}
                          />
                        </div>
                        <span className="strength-text" style={{ color: strength.color }}>
                          {strength.textKey ? t(strength.textKey) : ''}
                        </span>
                      </div>
                    )}
                  </div>

                  {activeTab === 'register' && (
                    <div className="form-group">
                      <label><i className="fas fa-shield-alt mr-2"></i>{t('auth.confirmPassword')}</label>
                      <input
                        type="password"
                        required
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="••••••••"
                      />
                    </div>
                  )}

                  <AuthCaptcha
                    recaptchaRef={recaptchaRef}
                    onChange={(token) => setCaptchaToken(token)}
                  />

                  <button type="submit" className="auth-submit-btn" disabled={busy}>
                    {activeTab === 'login' ? (
                      <><i className="fas fa-sign-in-alt mr-2"></i>{t('auth.submitLogin')}</>
                    ) : (
                      <><i className="fas fa-user-plus mr-2"></i>{t('auth.submitRegister')}</>
                    )}
                  </button>

                  <div className="auth-divider">
                    <span>{t('auth.or') || 'or'}</span>
                  </div>

                  <button type="button" className="auth-google-btn" onClick={handleGoogleLogin}>
                    <i className="fab fa-google mr-2"></i>
                    {t('auth.googleLogin') || 'Continue with Google'}
                  </button>
                </form>

                <div className="auth-footer">
                  {activeTab === 'login' ? (
                    <p>
                      {t('auth.noAccount')}{' '}
                      <span onClick={() => handleTabChange('register')}>
                        {t('auth.register')}
                      </span>
                    </p>
                  ) : (
                    <p>
                      {t('auth.haveAccount')}{' '}
                      <span onClick={() => handleTabChange('login')}>
                        {t('auth.login')}
                      </span>
                    </p>
                  )}
                </div>
              </>
            )}
            </GlassCard>
          </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AuthPage;
